import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FormattedMessage, useIntl } from "react-intl";
import { useAuthStore } from "@/lib/stores/authStore";
import { useAudienceStore } from "@/lib/stores/audienceStore";
import { showToast } from "@/lib/utils/toast";
import { isApiError } from "@/lib/api/client";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import { useLocale } from "@/contexts/IntlContext";
import registerImage from "@/assets/exercise-background-photos/register.png";
import { AuthFormCard } from "@/components/AuthFormCard";
import { AuthHeader } from "@/components/AuthHeader";
import { AuthBackButton } from "@/components/AuthBackButton";
import { AuthErrorAlert } from "@/components/AuthErrorAlert";
import { AuthTextInput } from "@/components/AuthTextInput";
import { AuthPasswordInput } from "@/components/AuthPasswordInput";
import { AuthFooterLink } from "@/components/AuthFooterLink";
import { NicknameSuggestions } from "@/components/NicknameSuggestions";

export const Route = createFileRoute("/auth/register")({
  component: RegisterComponent,
});

function RegisterComponent() {
  const navigate = useNavigate();
  const intl = useIntl();
  const { locale } = useLocale();

  const registerSchema = z
    .object({
      nickname: z
        .string()
        .min(1, intl.formatMessage({ id: "validation.nickname.required" })),
      gender: z.enum(["male", "female"]).nullable().optional(),
      password: z
        .string()
        .min(6, intl.formatMessage({ id: "validation.password.minLength" })),
      confirmPassword: z
        .string()
        .min(
          1,
          intl.formatMessage({ id: "validation.confirmPassword.required" })
        ),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: intl.formatMessage({
        id: "validation.confirmPassword.mustMatch",
      }),
      path: ["confirmPassword"],
    });

  type RegisterFormData = z.infer<typeof registerSchema>;

  const [nicknameServerError, setNicknameServerError] = useState<string | null>(
    null
  );
  const [nicknameSuggestions, setNicknameSuggestions] = useState<string[]>([]);

  const {
    user,
    register: registerUser,
    isLoading,
    error,
    clearError,
    redirectAfterAuth,
    setRedirectAfterAuth,
  } = useAuthStore();

  const { audience } = useAudienceStore();

  useEffect(() => {
    if (user) {
      const audienceLabel = user.audience
        ? intl.formatMessage({ id: `register.audience.${user.audience}` })
        : null;

      if (audienceLabel) {
        showToast.info(
          intl.formatMessage(
            { id: "toast.auth.alreadyLoggedInWithAudience" },
            { audience: audienceLabel }
          )
        );
      } else {
        showToast.info(
          intl.formatMessage({ id: "toast.auth.alreadyLoggedIn" })
        );
      }

      if (redirectAfterAuth) {
        navigate({ to: redirectAfterAuth });
        setRedirectAfterAuth(null);
      } else if (audience) {
        const targetUrl =
          audience === "CHILD" ? "/children/exercises" : "/adults/exercises";
        navigate({ to: targetUrl });
      }
    }
  }, [user, navigate, intl, redirectAfterAuth, setRedirectAfterAuth, audience]);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      clearError();
      const registrationData: any = {
        nickname: data.nickname,
        password: data.password,
        audience: audience || undefined,
      };

      if (data.gender !== undefined) {
        registrationData.gender = data.gender;
      }

      await registerUser(registrationData);
      showToast.success(
        intl.formatMessage(
          { id: "toast.auth.registerSuccess" },
          { nickname: data.nickname }
        )
      );
      if (redirectAfterAuth) {
        navigate({ to: redirectAfterAuth });
        setRedirectAfterAuth(null);
      } else if (audience) {
        const targetUrl =
          audience === "CHILD" ? "/children/exercises" : "/adults/exercises";
        navigate({ to: targetUrl });
      }
    } catch (error) {
      if (
        isApiError(error) &&
        error.status === 409 &&
        Array.isArray(error.data?.suggestions)
      ) {
        const message = error.message || "register.nickname.taken";
        setNicknameServerError(
          message.startsWith("validation.") || message.startsWith("register.")
            ? intl.formatMessage({ id: message })
            : message
        );
        setNicknameSuggestions(error.data.suggestions.slice(0, 5));
        clearError();
        return;
      }
    }
  };

  useEffect(() => {
    clearError();
  }, [clearError]);

  return (
    <div className="flex flex-col relative">
      <AuthHeader
        image={registerImage}
        imageAlt="Sign Up"
        title={<FormattedMessage id="register.createAccount" />}
        subtitle={<FormattedMessage id="register.description" />}
      />

      <AuthFormCard>
        <div className="flex flex-col">
          <AuthBackButton onClick={() => navigate({ to: "/app" })} />

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <AuthErrorAlert
                action={
                  error === "AUDIENCE_MISMATCH" ? (
                    <Link
                      to="/audience"
                      className="flex items-center gap-1 text-sm text-primary font-medium hover:underline"
                      onClick={() => clearError()}
                    >
                      {locale === "ar" ? (
                        <FaArrowRight className="w-3.5 h-3.5" />
                      ) : (
                        <FaArrowLeft className="w-3.5 h-3.5" />
                      )}

                      <FormattedMessage id="error.login.goBackToAudienceSelection" />
                    </Link>
                  ) : undefined
                }
              >
                {error.startsWith("validation.") ||
                error.startsWith("register.")
                  ? intl.formatMessage({ id: error })
                  : error}
              </AuthErrorAlert>
            )}

            <AuthTextInput
              {...register("nickname", {
                onChange: () => {
                  clearError();
                  if (nicknameServerError) setNicknameServerError(null);
                  if (nicknameSuggestions.length) setNicknameSuggestions([]);
                },
              })}
              type="text"
              placeholder={intl.formatMessage({
                id: "register.nickname.placeholder",
              })}
              invalid={!!nicknameServerError}
              disabled={isLoading}
              onFocus={clearError}
              error={errors.nickname?.message}
            >
              <NicknameSuggestions
                serverError={nicknameServerError}
                suggestions={nicknameSuggestions}
                showTitle
                onSelect={(s) => {
                  setValue("nickname", s, { shouldValidate: true });
                  setNicknameServerError(null);
                  setNicknameSuggestions([]);
                }}
              />
            </AuthTextInput>

            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {["male", "female"].map((gender) => (
                      <button
                        key={gender}
                        type="button"
                        onClick={() => {
                          clearError();
                          field.onChange(gender);
                        }}
                        className={`h-13 rounded-xl text-small transition-all duration-200 ${
                          field.value === gender
                            ? "bg-primary text-white"
                            : "bg-secondary text-dark-gray "
                        }`}
                        disabled={isLoading}
                      >
                        {gender === "male" && (
                          <FormattedMessage id="register.gender.male" />
                        )}
                        {gender === "female" && (
                          <FormattedMessage id="register.gender.female" />
                        )}
                      </button>
                    ))}
                  </div>
                  <button
                    key="prefer-not-to-say"
                    type="button"
                    onClick={() => {
                      clearError();
                      field.onChange(null);
                    }}
                    className={`h-13 w-full rounded-xl text-small transition-all duration-200 ${
                      field.value === null
                        ? "bg-primary text-white"
                        : "bg-secondary text-dark-gray "
                    }`}
                    disabled={isLoading}
                    data-testid="register-gender-unspecified"
                  >
                    <FormattedMessage id="register.gender.preferNotToSay" />
                  </button>
                  {errors.gender && (
                    <p className="text-dark text-small font-medium">
                      {errors.gender.message}
                    </p>
                  )}
                </div>
              )}
            />

            <AuthPasswordInput
              {...register("password")}
              placeholder={intl.formatMessage({
                id: "register.password.placeholder",
              })}
              disabled={isLoading}
              onFocus={clearError}
              error={errors.password?.message}
            />

            <AuthPasswordInput
              {...register("confirmPassword")}
              placeholder={intl.formatMessage({
                id: "register.confirmPassword.placeholder",
              })}
              disabled={isLoading}
              onFocus={clearError}
              error={errors.confirmPassword?.message}
            />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <FormattedMessage id="register.creating" />
              ) : (
                <FormattedMessage id="register.createAccount" />
              )}
            </Button>

            <AuthFooterLink
              promptId="register.alreadyHaveAccount"
              linkId="register.signIn"
              to="/auth/login"
            />
          </form>
        </div>
      </AuthFormCard>
    </div>
  );
}
